import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { useLocations } from '../queries';
import { Button, ErrorMessage, Field, Input, Notice, Select, number } from '../ui';
import type { Filters } from './LotFilters';

interface BulkResult {
  dryRun: boolean;
  matched: number;
  changed: number;
  skippedLocked: number;
  skippedNoPrice: number;
  sample: { id: number; label: string; before: string | null; after: string | null }[];
}

type ActionType = 'set_price' | 'adjust_price' | 'lock_price' | 'unlock_price' | 'set_price_rule' | 'set_location' | 'set_status';

const ACTIONS: { value: ActionType; label: string }[] = [
  { value: 'set_price', label: 'Set price to…' },
  { value: 'adjust_price', label: 'Change price by %…' },
  { value: 'lock_price', label: 'Lock price' },
  { value: 'unlock_price', label: 'Unlock price' },
  { value: 'set_price_rule', label: 'Keep price up to date with market…' },
  { value: 'set_location', label: 'Move stock to location…' },
  { value: 'set_status', label: 'Set status…' },
];

/** Numeric filter strings from the URL, as the API expects them. */
function filterBody(f: Filters) {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) {
    if (!v) continue;
    out[k] = ['qtyMin', 'qtyMax', 'colorId', 'categoryId'].includes(k) ? Number(v) : ['priceMin', 'priceMax'].includes(k) ? Number(v) : ['priceLocked', 'includeEmpty'].includes(k) ? v === 'true' : v;
  }
  return out;
}

export function BulkEdit({ filters, selectedIds, total }: { filters: Filters; selectedIds: number[]; total: number }) {
  const qc = useQueryClient();
  const locations = useLocations();
  const [type, setType] = useState<ActionType>('adjust_price');
  const [price, setPrice] = useState('');
  const [percent, setPercent] = useState('');
  const [locationId, setLocationId] = useState('');
  const [status, setStatus] = useState('live');
  const [rule, setRule] = useState('market');
  const [result, setResult] = useState<BulkResult | null>(null);

  const scope = selectedIds.length > 0 ? `${number(selectedIds.length)} selected lot${selectedIds.length === 1 ? '' : 's'}` : `all ${number(total)} matching lots`;

  function action() {
    switch (type) {
      case 'set_price':
        return { type, price };
      case 'adjust_price':
        return { type, percent: Number(percent) };
      case 'set_location':
        return { type, locationId: Number(locationId) };
      case 'set_status':
        return { type, status };
      case 'set_price_rule':
        return { type, rule: rule || null };
      default:
        return { type };
    }
  }

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<{ result: BulkResult }>('/api/v1/lots/bulk', {
        body: {
          target: selectedIds.length > 0 ? { ids: selectedIds } : { filter: filterBody(filters) },
          action: action(),
          dryRun,
        },
      }).then((r) => r.result),
    onSuccess: (r) => {
      setResult(r);
      if (!r.dryRun) {
        void qc.invalidateQueries({ queryKey: ['lots'] });
        void qc.invalidateQueries({ queryKey: ['dashboard'] });
      }
    },
  });

  const ready =
    total > 0 &&
    ((type === 'set_price' && price !== '') ||
      (type === 'adjust_price' && percent !== '' && Number.isFinite(Number(percent))) ||
      (type === 'set_location' && locationId !== '') ||
      type === 'lock_price' ||
      type === 'unlock_price' ||
      type === 'set_price_rule' ||
      type === 'set_status');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={`Change ${scope}`}>
          <Select
            value={type}
            onChange={(e) => {
              setType(e.target.value as ActionType);
              setResult(null);
            }}
          >
            {ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Select>
        </Field>
        {type === 'set_price' && (
          <Field label="Price">
            <Input inputMode="decimal" placeholder="0.10" value={price} onChange={(e) => setPrice(e.target.value)} />
          </Field>
        )}
        {type === 'adjust_price' && (
          <Field label="Percent" hint="-10 lowers by 10%">
            <Input inputMode="decimal" placeholder="-10" value={percent} onChange={(e) => setPercent(e.target.value)} />
          </Field>
        )}
        {type === 'set_location' && (
          <Field label="Location">
            <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">Choose…</option>
              {locations.data?.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.code}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {type === 'set_status' && (
          <Field label="Status" hint="Live and Stockroom lots are sent to BrickLink from the Sync page. Draft lots are not, and making one Draft does not remove it from BrickLink.">
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="live">Live</option>
              <option value="stockroom">Stockroom</option>
              <option value="draft">Draft</option>
            </Select>
          </Field>
        )}
        {type === 'set_price_rule' && (
          <Field label="Pricing rule" hint="Uses refreshed BrickLink market figures; choose Stop tracking to turn it off.">
            <Select value={rule} onChange={(e) => setRule(e.target.value)}><option value="market">In line with the market</option><option value="quick">Quick sale</option><option value="patient">Patient</option><option value="floor">Cost floor</option><option value="cost_plus">Cost plus 50%</option><option value="breakeven">Break even (your cost)</option><option value="">Stop tracking</option></Select>
          </Field>
        )}
        <Button variant="secondary" disabled={!ready || run.isPending} onClick={() => run.mutate(true)}>
          Preview
        </Button>
        <Button
          disabled={!ready || run.isPending || !result?.dryRun}
          title={result?.dryRun ? undefined : 'Preview first'}
          onClick={() => run.mutate(false)}
        >
          Apply
        </Button>
      </div>
      <ErrorMessage error={run.error} />
      {result && (
        <Notice tone={result.dryRun ? 'blue' : 'green'}>
          <p className="font-medium">
            {result.dryRun ? 'Preview: ' : 'Done: '}
            {number(result.changed)} of {number(result.matched)} lots {result.dryRun ? 'would change' : 'changed'}.
            {result.skippedLocked > 0 && ` ${number(result.skippedLocked)} skipped because their price is locked.`}
            {result.skippedNoPrice > 0 && ` ${number(result.skippedNoPrice)} skipped because they have no price.`}
          </p>
          {result.sample.length > 0 && (
            <ul className="mt-1 text-xs">
              {result.sample.slice(0, 5).map((s) => (
                <li key={s.id}>
                  {s.label}: {s.before ?? '—'} → {s.after ?? '—'}
                </li>
              ))}
            </ul>
          )}
        </Notice>
      )}
    </div>
  );
}
